import { useCallback, useMemo } from 'react';
import { useLiveStreamActiveUser } from '@core/livestream/hooks/useLiveStreamActiveUser';
import { pruneNilValues } from '@core/object';
import { sendPixelTrackingWithSession } from '@core/tracking';
import { useGetPlayerNestedTrackingPayload } from '@embed/components/VideoPlayer/Player3/hooks/useGetPlayerNestedTrackingPayload';
import { useVideoEl } from '@embed/components/VideoPlayer/Player3/hooks/useVideoEl';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { CLICK_REMIND_ME, CONFIRM_REMIND_ME, SELECT_TARGET_CALENDAR, SEND_CHAT, SEND_CHAT_PRIVATE, SEND_HEART, START_ACTIVE_WATCH, TRACK_CLICK_PRODUCT_MOMENT, } from '@player/features/liveStream/tracking/endpoints';
import { PLAYER_CLICK_SHARE_VIDEO } from '@player/tracking/endpoints';
/**
 *
 */
const useLiveStreamTracking = () => {
    const params = useStore((state) => state.params);
    const video = useStore((state) => state.video);
    const videoEl = useVideoEl();
    const { isActiveWatch } = useLiveStreamActiveUser(video);
    const getPlayerNestedTrackingPayload = useGetPlayerNestedTrackingPayload();
    const liveStreamId = video?.live_stream_id ? video?.live_stream_id : null;
    const liveStream = useStore(useCallback((state) => (liveStreamId ? state.liveStreams[liveStreamId] : {}), [liveStreamId]));
    return useMemo(() => {
        if (!video || !liveStreamId) {
            return null;
        }
        const { live_stream_id: _live_stream_id, live_stream_status } = video;
        const highlighted_entity = liveStream
            ? liveStream.highlighted_entity
            : undefined;
        const prepData = (video_id, extra) => ({
            event_properties: pruneNilValues({
                ...getPlayerNestedTrackingPayload(),
                _video_id: video_id,
                elapsed_time: videoEl.elapsedTime,
                highlighted_entity: highlighted_entity
                    ? {
                        _entity_id: highlighted_entity?.entity_id,
                        entity_type: highlighted_entity?.entity_type,
                    }
                    : undefined,
                _live_stream_id,
                live_stream_status,
                is_active: isActiveWatch,
                ...extra,
            }),
        });
        const sendLivestreamTracking = (endpoint) => (video_id, extra = {}) => {
            sendPixelTrackingWithSession({
                endpoint,
                data: prepData(video_id, extra),
                host: params?.api_host_tracking,
            });
        };
        return {
            startActiveWatch: sendLivestreamTracking(START_ACTIVE_WATCH),
            sendChat: sendLivestreamTracking(SEND_CHAT),
            sendChatPrivate: sendLivestreamTracking(SEND_CHAT_PRIVATE),
            sendHeart: sendLivestreamTracking(SEND_HEART),
            trackClickShareVideo: sendLivestreamTracking(PLAYER_CLICK_SHARE_VIDEO),
            trackClickProductMoment: sendLivestreamTracking(TRACK_CLICK_PRODUCT_MOMENT),
            clickRemindMe: sendLivestreamTracking(CLICK_REMIND_ME),
            confirmRemindMe: sendLivestreamTracking(CONFIRM_REMIND_ME),
            selectTargetCalendar: sendLivestreamTracking(SELECT_TARGET_CALENDAR),
        };
    }, [
        video,
        videoEl,
        liveStreamId,
        liveStream,
        getPlayerNestedTrackingPayload,
        isActiveWatch,
        params,
    ]);
};
export default useLiveStreamTracking;
